"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

type ComboboxProps = {
  label: string
  placeholder: string
  options: {value: string, label: string}[]
  value?: string
  onChange: (value: string) => void
}

export function ComboBox({label, placeholder, options, value: controlledValue, onChange}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState(controlledValue || "")
  
  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue

  return (
    <div className="flex flex-col w-full gap-2">
      
      <h4>{label}</h4>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild style={{
          border: "1px solid" 
        }}>
          <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {value
            ? options.find((option) => option.value === value)?.label
            : "Select " + placeholder + "..."}
          <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="p-0">
        <Command>
          <CommandInput placeholder={"Search " + placeholder + "..."} />
          <CommandList>
            <CommandEmpty>No {placeholder} found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    const newValue = currentValue === value ? "" : currentValue
                    if (controlledValue !== undefined) {
                      // Controlled component - call onChange
                      onChange(newValue)
                    } else {
                      // Uncontrolled component - update internal state
                      setInternalValue(newValue)
                      onChange(newValue)
                    }
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
    </div>
  )
}
